import {
  getApprovalFlowItemTypeForWorkbook,
  isApprovalFlowSupportedWorkbook,
} from '@/administration/management/approvalFlow/approvalFlowItemTypes.utilities';
import { sqWorkbookStore } from '@/core/core.stores';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import type {
  WorkbookApprovalFlow,
  WorkbookApprovalFlowAssignment,
  WorkbookApprovalFlowStep,
} from '@/worksheet/worksheet.types';

export type UseApprovalFlowsResult = {
  approvalFlow: WorkbookApprovalFlow | null;
  /** Every flow on the workbook. For managers this is every active flow. Apply offers the ones that have steps. */
  approvalFlows: WorkbookApprovalFlow[];
  approvalFlowAssignments: WorkbookApprovalFlowAssignment[];
  approvedSteps: WorkbookApprovalFlowStep[] | undefined;
  pendingApprovalSteps: WorkbookApprovalFlowStep[] | undefined;
  nextStep: WorkbookApprovalFlowStep | undefined;
  futureStep: WorkbookApprovalFlowStep | undefined;
  hasApprovalFlowAttached: boolean;
  /** Translation key of the open workbook's item type name, e.g. "Analysis". */
  itemTypeLabelKey: string | undefined;
  showApplyBtn: boolean;
  showStepDropdown: boolean;
  workbookId: string;
  approved: boolean | undefined;
};

export const useApprovalFlows = (): UseApprovalFlowsResult => {
  const {
    approvalFlows,
    approvalFlowAssignments,
    approvalFlowAttachments,
    workbookId,
    effectivePermissions,
    isAnalysis,
    isTopic,
    isArchived,
  } = useFluxPaths(sqWorkbookStore, [
    'approvalFlows',
    'approvalFlowAssignments',
    'approvalFlowAttachments',
    'workbookId',
    'effectivePermissions',
    'isAnalysis',
    'isTopic',
    'isArchived',
  ]);
  // GraphQL returns the attached flow plus any other flows the reader can access through a step, newest-first.
  // The header must follow the workbook's attachment, not list order, or it can display and act on an unrelated flow.
  const attachedFlowId = approvalFlowAttachments[0]?.approvalFlowId;
  const approvalFlow =
    (attachedFlowId != null ? approvalFlows.find((flow) => flow.id === attachedFlowId) : approvalFlows[0]) ?? null;
  const pendingApprovalSteps = approvalFlow?.steps.filter(({ status }) => status === 'PENDING');
  const approvedSteps = approvalFlow?.steps.filter(({ status }) => status === 'APPROVED');
  const nextStep = pendingApprovalSteps?.[0];
  const futureStep = pendingApprovalSteps?.[1];
  const hasApprovalFlowAttached = approvalFlowAttachments.length > 0;
  const isSupportedWorkbookType = isApprovalFlowSupportedWorkbook({ isAnalysis, isTopic });
  // A flow with no steps cannot be applied, and an empty step list would otherwise count as fully approved.
  const hasApplicableFlow = approvalFlows.some((flow) => flow.steps.length > 0);
  const showApplyBtn =
    hasApplicableFlow &&
    isSupportedWorkbookType &&
    !isArchived &&
    effectivePermissions.manage &&
    !hasApprovalFlowAttached;
  // The dropdown reports the progress of the flow on this workbook, so it only means anything once the flow is
  // attached: step access alone says nothing about this workbook. Acting on a step still requires write on that step,
  // which the dropdown enforces per option.
  const showStepDropdown =
    approvalFlow != null &&
    approvalFlow.steps.length > 0 &&
    isSupportedWorkbookType &&
    !isArchived &&
    hasApprovalFlowAttached;
  const approved =
    hasApprovalFlowAttached && approvalFlow != null && approvalFlow.steps.length > 0
      ? approvalFlow.steps.every((step) => step.status === 'APPROVED')
      : undefined;

  return {
    approvedSteps,
    pendingApprovalSteps,
    approvalFlow,
    approvalFlows,
    approvalFlowAssignments,
    hasApprovalFlowAttached,
    itemTypeLabelKey: getApprovalFlowItemTypeForWorkbook({ isAnalysis, isTopic })?.labelKey,
    nextStep,
    futureStep,
    showApplyBtn,
    showStepDropdown,
    workbookId,
    approved,
  };
};
